import { messageTemplateVariables } from 'ai-constants'
import React from 'react'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'ui/form'
import { Button } from 'ui/button'
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover'
import { ScrollArea } from 'ui/scroll-area'
import { Separator } from 'ui/separator'
import { Textarea } from 'ui/textarea'
import { cn } from 'ui/utils'
import { useMessageTemplateForm } from '../hooks/useMessageTemplateForm'
import { Input } from 'ui/input'
import {
  DeliveryChannel,
  IMessageTemplate,
} from '../providers/MessageTemplate.provider'
import { IMessageTemplateSchema } from '../schemas/message-template.schema'
import { MessageTemplatePreview } from './MessageTemplatePreviews'
import { TranslatorOnlyString } from './Translator'
import { IconPlus } from '@tabler/icons-react'
import { useTranslation } from 'i11n/client'

const assignInputRef = (
  fieldRef: (instance: HTMLInputElement | null) => void,
  customRef: React.MutableRefObject<HTMLInputElement | null>,
  element: HTMLInputElement | null,
) => {
  fieldRef(element)
  customRef.current = element
}

const assignTextareaRef = (
  fieldRef: (instance: HTMLTextAreaElement | null) => void,
  customRef: React.MutableRefObject<HTMLTextAreaElement | null>,
  element: HTMLTextAreaElement | null,
) => {
  fieldRef(element)
  customRef.current = element
}

const AddVariable = (props: { onVariableSelected: (path: string) => void }) => {
  const t = useTranslation()
  const [open, setOpen] = React.useState(false)
  const handleTriggerMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
    },
    [],
  )
  const handleOpenAutoFocus = React.useCallback((event: Event) => {
    event.preventDefault()
  }, [])
  const handleVariableSelected = React.useCallback(
    (path: string) => {
      props.onVariableSelected(path)
      setOpen(false)
    },
    [props.onVariableSelected],
  )

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='link'
            className='text-gray-600 font-normal hover:no-underline text-foreground text-xs pl-0'
            size='sm'
            onMouseDown={handleTriggerMouseDown}
          >
            <IconPlus className='mr-2 h-4 w-4' />
            {t('Añadir variable')}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='p-2 pr-2'
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <ScrollArea
            orientation='vertical'
            className='max-h-60 h-full flex flex-col'
          >
            <div className='flex flex-col pr-4'>
              {messageTemplateVariables.map((ele) => {
                return (
                  <div key={ele.group}>
                    <p className='font-bold text-sm'>
                      {TranslatorOnlyString({
                        children: ele.group,
                      })}
                    </p>
                    {ele.paths.map((path) => {
                      if (path.path)
                        return (
                          <MessageTemplateVariableOption
                            key={path.path}
                            path={`${ele.group}.${path.path}`}
                            onVariableSelected={handleVariableSelected}
                          >
                            {TranslatorOnlyString({
                              children: path.path,
                            })}
                          </MessageTemplateVariableOption>
                        )

                      return (
                        <div key={path.group}>
                          <Separator className='my-1' />
                          <p className='font-bold text-sm'>
                            {TranslatorOnlyString({
                              children: path.group,
                            })}
                          </p>
                          {path.paths.map((nestedPath) => {
                            return (
                              <MessageTemplateVariableOption
                                key={nestedPath.path}
                                path={`${ele.group}.${path.group}.${nestedPath.path}`}
                                onVariableSelected={handleVariableSelected}
                              >
                                {TranslatorOnlyString({
                                  children: nestedPath.path,
                                })}
                              </MessageTemplateVariableOption>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  )
}

const MessageTemplateVariableOption = (props: {
  children: React.ReactNode
  path: string
  onVariableSelected: (path: string) => void
}) => {
  const { children, onVariableSelected, path } = props
  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault()
      onVariableSelected(path)
    },
    [onVariableSelected, path],
  )
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (event.detail) return

      onVariableSelected(path)
    },
    [onVariableSelected, path],
  )

  return (
    <button
      className='w-full rounded-sm px-2 py-1 text-left text-xs transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      type='button'
    >
      {children}
    </button>
  )
}

const FormFieldFooterMessage = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <FormDescription className='m-0 flex-shrink-0 text-xs'>
      {children}
    </FormDescription>
  )
}

const FormFieldFooter = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn(
        'h-6 mt-2 flex flex-row justify-between items-center',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const MessageTemplateEditorV2 = (props: {
  gatewayStatus?: IMessageTemplate['gatewayStatus']
  children?: React.ReactNode
  channel?: DeliveryChannel
  formId: string
  onSubmit: (data: IMessageTemplateSchema) => void
  defaultValues?: Partial<IMessageTemplateSchema>
}) => {
  const t = useTranslation()
  const {
    customButtonLabelRef,
    fields,
    handleSubmit,
    onAddVariable,
    customSubjectRef,
    customBodyRef,
    updateVariableFieldCursorPosition,
    form,
  } = useMessageTemplateForm({
    channel: props.channel,
    defaultValues: props.defaultValues,
  })
  const handleSubjectCursorPositionChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      updateVariableFieldCursorPosition('subject', event.currentTarget)
    },
    [updateVariableFieldCursorPosition],
  )
  const handleBodyCursorPositionChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
      updateVariableFieldCursorPosition('body', event.currentTarget)
    },
    [updateVariableFieldCursorPosition],
  )

  return (
    <div className='grid grid-cols-2 gap-6 divide-x'>
      <div>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(props.onSubmit)}
            id={props.formId}
            className='space-y-5 flex flex-col'
          >
            <FormField
              control={form.control}
              name={'subject'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel className='text-sm'>
                      {t('Encabezado *')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        onClick={handleSubjectCursorPositionChange}
                        onFocus={handleSubjectCursorPositionChange}
                        onKeyUp={handleSubjectCursorPositionChange}
                        onSelect={handleSubjectCursorPositionChange}
                        ref={(e) => {
                          assignInputRef(field.ref, customSubjectRef, e)
                        }}
                        size='sm'
                      />
                    </FormControl>
                    <FormFieldFooter>
                      <AddVariable
                        onVariableSelected={(path) =>
                          onAddVariable('subject', path)
                        }
                      />
                      <FormFieldFooterMessage>
                        {t('Máximo 60 caracteres')}
                      </FormFieldFooterMessage>
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />

            <FormField
              control={form.control}
              name={'body'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel className='text-sm'>{t('Mensaje *')}</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={field.value ?? ''}
                        className='h-48'
                        onClick={handleBodyCursorPositionChange}
                        onFocus={handleBodyCursorPositionChange}
                        onKeyUp={handleBodyCursorPositionChange}
                        onSelect={handleBodyCursorPositionChange}
                        ref={(e) => {
                          assignTextareaRef(field.ref, customBodyRef, e)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormFieldFooter>
                      <AddVariable
                        onVariableSelected={(path) =>
                          onAddVariable('body', path)
                        }
                      />
                      <FormFieldFooterMessage>
                        {t('Máximo 1024 caracteres')}
                      </FormFieldFooterMessage>
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />

            <FormField
              control={form.control}
              name={'buttonLabel'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel className='text-sm'>{t('Botón')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        ref={(e) => {
                          assignInputRef(field.ref, customButtonLabelRef, e)
                        }}
                        size='sm'
                      />
                    </FormControl>
                    <FormMessage />
                    <FormFieldFooter className='justify-end'>
                      <FormFieldFooterMessage>
                        {t('Máximo 60 caracteres')}
                      </FormFieldFooterMessage>
                    </FormFieldFooter>
                  </FormItem>
                )
              }}
            />
          </form>
        </Form>
      </div>
      <div className='pl-6 pt-2 min-h-[550px] '>
        <div className='h-full flex flex-col justify-between items-end'>
          <MessageTemplatePreview fields={fields} channel={props.channel} />
          {props.children}
        </div>
      </div>
    </div>
  )
}
